import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { axiosInstance } from "../../lib/axios";
import {

  Target,
  TrendingUp,
  Smartphone,
  Zap,
  Code,
  Globe,
  Headphones,
  Rocket,
  Shield,
  BarChart,

Lightbulb,
FileCheck,

} from "lucide-react";

import ModernTemplate from "./templates/ModernTemplate";
import ClassicTemplate from "./templates/ClassicTemplate";
import MinimalTemplate from "./templates/MinimalTemplate";
import InfographicTemplate from "./templates/InfographicTemplate";

export type Proposal = {
  template?: "modern" | "classic" | "minimal" | "infographic";
  title?: string;
  sections?: any[];
  fullName?: string;
  [key: string]: any;
};

const dynamicIconMap: Record<string, any> = {
  brand: Target,
  branding: Target,
  identity: Target,

  engagement: TrendingUp,
  conversion: TrendingUp,
  growth: TrendingUp,

  speed: Zap,
  performance: Zap,
  optimization: Zap,

  mobile: Smartphone,
  responsive: Smartphone,

  development: Code,
  fullstack: Code,
  backend: Code,
  frontend: Code,

  seo: Globe,
  search: Globe,
  visibility: Globe,

  support: Headphones,
  maintenance: Headphones,

  launch: Rocket,
  deployment: Rocket,

  security: Shield,
  protection: Shield,

  analytics: BarChart,
  data: BarChart,

  ui: Lightbulb,
  ux: Lightbulb,
  design: Lightbulb,

  funnel: FileCheck,
  lead: FileCheck,
};

const getIconFromKeyword = (text: string) => {
  if (!text) return Target;

  const lower = text.toLowerCase();

  for (const key in dynamicIconMap) {
    if (lower.includes(key)) {
      return dynamicIconMap[key];
    }
  }

  return Target;
};
const PublicProposalView = () => {
  const { token } = useParams();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  // Full-page image slider for proposal images (portfolio, showcase, …).
  const [gallery, setGallery] = useState<string[]>([]);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);
  const zoomSrc = zoomIdx !== null ? gallery[zoomIdx] : null;
  const step = (dir: number) =>
    setZoomIdx((i) => (i === null || !gallery.length ? i : (i + dir + gallery.length) % gallery.length));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        const res = await axiosInstance.get(`/proposals/public/${token}`);
        setProposal(res.data);
        if (res.data?.tenantBrand) {
          const { applyBranding } = await import("../../lib/tenant");
          applyBranding(res.data.tenantBrand);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchProposal();
  }, [token]);

  if (loading) return <div className="p-10 text-center">Loading...</div>;
  if (!proposal) return <div className="p-10 text-center">Not found</div>;

  const tpl =
    proposal.template === "infographic" ? (
      <InfographicTemplate proposal={proposal} persona={proposal.accountPersona} />
    ) : proposal.template === "classic" ? (
      <ClassicTemplate proposal={proposal} getIconFromKeyword={getIconFromKeyword} />
    ) : proposal.template === "minimal" ? (
      <MinimalTemplate proposal={proposal} />
    ) : (
      <ModernTemplate proposal={proposal} />
    );

  return (
    <div
      // Any meaningful image in the proposal opens in a zoom overlay.
      onClickCapture={(e) => {
        const t = e.target as HTMLImageElement;
        if (t?.tagName === "IMG" && t.src && (t.naturalWidth >= 120 || t.clientWidth >= 120)) {
          e.preventDefault();
          e.stopPropagation();
          // Build the slider set from every meaningful image on the page.
          const imgs = Array.from(document.querySelectorAll("img"))
            .filter((im) => im.src && (im.naturalWidth >= 120 || im.clientWidth >= 120))
            .map((im) => im.src);
          const uniq = Array.from(new Set(imgs));
          setGallery(uniq);
          setZoomIdx(Math.max(0, uniq.indexOf(t.src)));
        }
      }}
      onKeyDown={(e) => {
        if (zoomIdx === null) return;
        if (e.key === "ArrowRight") step(1);
        if (e.key === "ArrowLeft") step(-1);
        if (e.key === "Escape") setZoomIdx(null);
      }}
      tabIndex={-1}
      // Readability: proposals render ~8% larger across the board.
      style={{ zoom: 1.08 } as any}
      className="[&_img]:cursor-zoom-in"
    >
      {/* The freelancer profile this proposal comes from — the same identity
          the client knows from the freelance platform. The infographic template
          carries the persona on its own cover, so the banner is skipped there. */}
      {proposal.accountPersona?.name && proposal.template !== "infographic" && (
        <div className="max-w-3xl mx-auto px-6 pt-6">
          <div className="flex items-center gap-3 rounded-xl border border-black/10 bg-white/70 px-4 py-3 shadow-sm">
            {proposal.accountPersona.profilePic ? (
              <img
                src={proposal.accountPersona.profilePic}
                alt={proposal.accountPersona.name}
                className="h-11 w-11 rounded-full object-cover shrink-0 cursor-default"
              />
            ) : (
              <div className="h-11 w-11 rounded-full bg-slate-100 grid place-items-center text-sm font-bold text-slate-500 shrink-0">
                {proposal.accountPersona.name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="leading-tight">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Prepared by</p>
              <p className="text-[15px] font-bold text-slate-800">{proposal.accountPersona.name}</p>
            </div>
          </div>
        </div>
      )}
      {tpl}
      {zoomSrc && (
        <div
          className="fixed inset-0 z-[999] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={(e) => { e.stopPropagation(); setZoomIdx(null); }}
        >
          <img src={zoomSrc} alt="" className="max-w-full max-h-full rounded-lg shadow-2xl object-contain" />
          {gallery.length > 1 && (
            <>
              <button
                aria-label="Previous image"
                onClick={(e) => { e.stopPropagation(); step(-1); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl"
              >&#8249;</button>
              <button
                aria-label="Next image"
                onClick={(e) => { e.stopPropagation(); step(1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl"
              >&#8250;</button>
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-[12px] font-semibold">
                {(zoomIdx ?? 0) + 1} / {gallery.length}
              </span>
            </>
          )}
          <button aria-label="Close" className="absolute top-4 right-5 text-white/80 hover:text-white text-4xl leading-none">×</button>
        </div>
      )}
    </div>
  );
};

export default PublicProposalView;

